import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Modal, Spin } from 'antd';
import ChartComponent, { CHART_TYPES } from '../../../components/chartComponent/ChartComponent';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { ENVIRONMENT_CONFIG } from '../../../constants/environmentConfig';
import fetcher from '../../../helpers/fetcher';

export interface IAlertCard {
  title: string;
  location: string;
  basketName: string;
  reportedPrice: number;
  triggeredAt: number;
  reason: string;
  alertId?: number;
  alertConfigId?: number;
  productId?: number;
  alertThreshold?: string;
  alertThresholdValue?: number;
  alertCondition?: string;
}

interface PriceInstance {
  id: number;
  price: number;
  reportedTime: number;
  inspection: {
    location: {
      district: string;
      city: string;
    };
  };
}

const AlertCard = (props: IAlertCard) => {
  const {
    title,
    reportedPrice,
    triggeredAt,
    reason,
    location,
    basketName,
    alertId,
    alertConfigId,
    productId,
    alertThreshold,
    alertThresholdValue,
    alertCondition,
  } = props;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [priceInstances, setPriceInstances] = useState<PriceInstance[]>([]);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation(['advancedAnalysis']);

  const handleSeeDetails = async () => {
    if (!alertConfigId || !productId) {
      console.error('Missing alertConfigId or productId');
      return;
    }

    setIsModalVisible(true);
    setLoading(true);

    try {
      const response = await fetcher.get(
        API_ENDPOINTS.PRICE.GET_PRICE_INSTANCES,
        { alertId, alertConfigId, productId },
        undefined,
        ENVIRONMENT_CONFIG.ANALYSIS_SERVICE
      );
      if (response.statusText === 'SUCCESS') {
        setPriceInstances(response.data);
      } else {
        console.error('Failed to fetch price instances');
        setPriceInstances([]);
      }
    } catch (error) {
      console.error('Error fetching price instances:', error);
      setPriceInstances([]);
    } finally {
      setLoading(false);
    }
  };

  const formatChartData = (instances: PriceInstance[]) => {
    return instances.map((instance) => {
      let timestamp = instance.reportedTime;

      if (typeof timestamp === 'string') {
        timestamp = parseInt(timestamp);
      }

      if (timestamp < 1000000000000) {
        timestamp = timestamp * 1000;
      }

      const date = new Date(timestamp);

      if (isNaN(date.getTime())) {
        console.error('Invalid timestamp:', instance.reportedTime);
        return {
          date: 'Invalid Date',
          time: 'Invalid Time',
          price: instance.price,
          location: `${instance.inspection.location.district}, ${instance.inspection.location.city}`,
          fullDate: 'Invalid Date',
        };
      }

      return {
        date: date.toLocaleDateString(),
        time: date.toLocaleTimeString(),
        price: instance.price,
        location: `${instance.inspection.location.district}, ${instance.inspection.location.city}`,
        fullDate: date.toLocaleString(),
      };
    });
  };

  const chartData = formatChartData(priceInstances);

  return (
    <>
      <div className="flex h-full w-full min-w-0 flex-col space-y-3 rounded-md border border-[#BFDBFE] bg-[#eff6ff] p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <h5 className="font-semibold text-[#111827]">{title}</h5>
            <p className="text-font-color">{location}</p>
          </div>
        </div>
        <div className="flex flex-row flex-wrap items-center gap-1">
          {basketName.split(',').map((name, index) => (
            <p
              key={index}
              className="rounded-full bg-primary-red px-3 py-0.5 text-xs font-semibold text-white"
            >
              {name.trim()}
            </p>
          ))}
        </div>
        <div>
          <h4 className="text-sm text-font-color">{t('triggeredFor')}</h4>
          <p className="text-base text-[#0A0A0A]">L {reportedPrice}</p>
        </div>

        <div>
          <h4 className="text-sm text-font-color">
            {t('triggeredAt')}:{' '}
            {(() => {
              if (!triggeredAt) return 'No date available';

              let timestamp = triggeredAt;

              if (typeof timestamp === 'string') {
                timestamp = parseInt(timestamp);
              }

              if (timestamp < 1000000000000) {
                timestamp = timestamp * 1000;
              }

              const date = new Date(timestamp);

              if (isNaN(date.getTime())) {
                return `Invalid date (${triggeredAt})`;
              }

              return date.toLocaleString();
            })()}
          </h4>
        </div>

        <div className="flex-grow">
          <h4 className="text-sm text-font-color">{t('reason')}</h4>
          <p className="break-words text-base text-[#0A0A0A]">
            {reason.includes('Alert triggered')
              ? 'Price threshold exceeded - Alert condition met'
              : reason}
          </p>
        </div>

        <div className="flex flex-col gap-2 pt-3 sm:flex-row sm:justify-between">
          <button
            className="w-full rounded-md bg-black px-3 py-1 text-base font-medium text-white sm:w-auto"
            onClick={handleSeeDetails}
          >
            {t('seeDetails')}
          </button>
        </div>
      </div>

      <Modal
        title={t('priceInstancesTitle', {product: title})}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Spin size="large" />
          </div>
        ) : (
          <div className="space-y-6 p-6">
            <div className="space-y-2 text-sm text-gray-600">
              <p>
                <strong>{t('presentations')}:</strong> {title}
              </p>
              <p>
                <strong>{t('basket')}:</strong> {basketName}
              </p>
              <p>
                <strong>{t('location')}:</strong> {location}
              </p>
            </div>

            {alertThreshold && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <h4 className="mb-2 font-semibold text-blue-800">{t('alertConfiguration')}</h4>
                <div className="text-sm text-blue-700">
                  <p>
                    <strong>{t('condition')}:</strong> {alertCondition || t('priceThreshold')}
                  </p>
                  <p>
                    <strong>{t('threshold')}:</strong> L {alertThreshold}
                  </p>
                </div>
              </div>
            )}

            <div className="border-t border-gray-200 pt-4">
              <h4 className="mb-3 font-semibold text-gray-800">{t('priceInstancesTimeline')}</h4>

              {chartData.length > 0 ? (
                <div className="h-80">
                  <ChartComponent
                    title=""
                    type={CHART_TYPES.BAR}
                    series={[
                      {
                        name: 'Price (L)',
                        data: chartData.map((item) => item.price),
                      },
                    ]}
                    options={{
                      chart: {
                        type: 'bar',
                        toolbar: {
                          show: false,
                        },
                      },
                      xaxis: {
                        categories: chartData.map((item) => `${item.date} ${item.time}`),
                        labels: {
                          rotate: -45,
                          rotateAlways: false,
                          style: {
                            fontSize: '10px',
                          },
                        },
                      },
                      yaxis: {
                        title: {
                          text: t('priceYaxis'),
                        },
                      },
                      annotations: alertThresholdValue
                        ? {
                            yaxis: [
                              {
                                y: alertThresholdValue,
                                borderColor: '#EF4444',
                                borderWidth: 2,
                                strokeDashArray: 5,
                                label: {
                                  borderColor: '#EF4444',
                                  style: {
                                    color: '#EF4444',
                                    background: '#FEF2F2',
                                  },
                                  text: ``,
                                },
                              },
                            ],
                          }
                        : undefined,
                      tooltip: {
                        custom: function ({ dataPointIndex }) {
                          const data = chartData[dataPointIndex];
                          return `
                             <div class="bg-white p-3 border rounded shadow-lg">
                               <p class="font-semibold">${data.fullDate}</p>
                               <p>${t('price')}: L ${data.price}</p>
                               <p>${t('location')}: ${data.location}</p>
                             </div>
                           `;
                        },
                      },
                      colors: ['#3B82F6'],
                    }}
                    height={300}
                    maxHeight={300}
                  />
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">
                  {t('noPriceInstances')}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default AlertCard;
